import { AppBar, Box, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import Hamburger from 'hamburger-react';
import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import { App, BannerMobileAlertMessagesCollection, Header, Maybe } from '@/types/generated';
import { getWrapperStyles, logoStyles, iconStyles, toolbarHeaderStyles, burgerAndLogoWrapperStyles, headerNavRightStyles } from './HeaderMobile.styles';
import GroupNavList from '../GroupNavList/GroupNavList';
import { bannerConfigExtractor } from '../HeaderDesktop/HeaderDesktop';
import LocationNavBar from '../LocationNavBar/LocationNavBar';
import MobileMenu from './MobileMenu/MobileMenu';
import NavigationBarItem from '../NavigationBar/NavigationBarItem/NavigationBarItem';
import WelcomeBackBanner from '../WelcomeBackBanner/WelcomeBackBanner';
import Banner from '@shared-components/components/Banner/Banner';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import TAGLink from '@shared-components/components/TAGLink';
import useIsLoggedIn from '@src/hooks/useIsLoggedIn';
interface IHeaderMobile {
  headerData: App['header'];
  logoUrl?: Maybe<string>;
  logoHref: string;
  displayHeaderGroupMenu?: boolean;
}
const RenderNavList = ({
  navList,
  isUtilityNavigation,
  linkColor,
  showIconText
}: {
  navList: Header['navigationLinksCollection'] | Header['secondaryNavigationLinksCollection'];
  isUtilityNavigation?: boolean;
  linkColor?: string;
  showIconText?: boolean;
}) => {
  return <>
			{navList?.items?.map((item, index) => item && <NavigationBarItem key={item.sys.id} item={item} itemIndex={index} isMobile={true} linkColor={linkColor} isUtilityNavigation={isUtilityNavigation} iconSize={2} showIconText={showIconText} navLinkStyles={{
      display: 'inline-flex',
      alignItems: 'center'
    }} />)}
		</>;
};
export default function HeaderMobile({
  logoUrl,
  logoHref = '/',
  headerData,
  displayHeaderGroupMenu
}: Readonly<IHeaderMobile>) {
  const theme = useTheme();
  const appBarRef = useRef<HTMLDivElement>(null);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const router = useRouter();
  const isDesktopViewport = useMediaQuery(theme.breakpoints.up('md'));
  const {
    topAlertBannerMessages,
    bottomAlertBannerMessages,
    showTopAlertBannerMessages,
    showBottomAlertBannerMessages
  } = bannerConfigExtractor(headerData, true);
  const {
    isLoggedIn,
    firstName,
    checked
  } = useIsLoggedIn();
  useEffect(() => {
    if (isNavMenuOpen) {
      document.documentElement.style.overflowY = 'hidden';
    } else {
      document.documentElement.style.overflowY = 'inherit';
    }
  }, [isNavMenuOpen]);

  // HeaderDesktop/HeaderMobile are both mounted at once and toggled via CSS breakpoints, so resizing across
  // the breakpoint doesn't unmount this menu; close it explicitly instead of leaving it open behind HeaderDesktop.
  useEffect(() => {
    setIsNavMenuOpen(false);
  }, [isDesktopViewport]);
  useEffect(() => {
    const handleRouteChange = () => {
      setIsNavMenuOpen(false);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);
  return <>
			<AppBar sx={getWrapperStyles(theme)} ref={appBarRef} data-testid="header_with_location_nav_mobile" data-sentry-element="AppBar" data-sentry-source-file="HeaderMobile.tsx">
				{showTopAlertBannerMessages && <Banner dataTestId="header_mobile_top_alert_banner" variant={'Alert Banner'} mobileAlertMessagesCollection={topAlertBannerMessages as BannerMobileAlertMessagesCollection} />}
				<Toolbar disableGutters sx={toolbarHeaderStyles} data-testid="header_with_location_nav_mobile_actions" data-sentry-element="Toolbar" data-sentry-source-file="HeaderMobile.tsx">
					<Box sx={burgerAndLogoWrapperStyles} data-sentry-element="Box" data-sentry-source-file="HeaderMobile.tsx">
						<TAGLink href={logoHref} data-sentry-element="TAGLink" data-sentry-source-file="HeaderMobile.tsx">
							<ImageWrapper src={logoUrl} width="116px" height="auto" sx={logoStyles} data-testid="img_logo_mobile" alt="brand logo" data-sentry-element="ImageWrapper" data-sentry-source-file="HeaderMobile.tsx" />
						</TAGLink>
						<Box sx={headerNavRightStyles} data-sentry-element="Box" data-sentry-source-file="HeaderMobile.tsx">
							{headerData?.showSecondaryNavInMobileToolbar && (!checked || !isLoggedIn) && <RenderNavList navList={headerData.secondaryNavigationLinksCollection} />}
							{checked && isLoggedIn && <WelcomeBackBanner firstName={firstName} />}
							<IconButton sx={iconStyles} data-testid="header_with_location_nav_burger_button" tabIndex={-1} aria-label="Open menu" data-sentry-element="IconButton" data-sentry-source-file="HeaderMobile.tsx">
								<Hamburger size={24} toggled={isNavMenuOpen} toggle={setIsNavMenuOpen} color={theme.palette.text.interactive} label="open menu" data-sentry-element="Hamburger" data-sentry-source-file="HeaderMobile.tsx" />
							</IconButton>
						</Box>
					</Box>
				</Toolbar>
				<LocationNavBar schedulingButtonText={headerData?.schedulingButtonText} schedulingButtonTextMobile={headerData?.schedulingButtonTextMobile} schedulingButtonUrl={headerData?.schedulingButtonUrl} locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl} searchFieldTitle={headerData?.searchFieldTitle} isMobile data-sentry-element="LocationNavBar" data-sentry-source-file="HeaderMobile.tsx" />
				<div ref={appBarRef} />
				{showBottomAlertBannerMessages && <Banner dataTestId="header_mobile_bottom_alert_banner" variant={'Alert Banner'} mobileAlertMessagesCollection={bottomAlertBannerMessages as BannerMobileAlertMessagesCollection} />}
			</AppBar>
			<MobileMenu appBarRef={appBarRef} onClose={() => setIsNavMenuOpen(false)} open={isNavMenuOpen} data-sentry-element="MobileMenu" data-sentry-source-file="HeaderMobile.tsx">
				{displayHeaderGroupMenu ? <GroupNavList headerData={headerData} isMobile toolbarRef={appBarRef} /> : <RenderNavList navList={headerData?.navigationLinksCollection} isUtilityNavigation={true} linkColor="text.interactive" />}
				{!headerData?.showSecondaryNavInMobileToolbar && (!checked || !isLoggedIn) && <RenderNavList navList={headerData?.secondaryNavigationLinksCollection} isUtilityNavigation={true} linkColor="text.interactive" showIconText={true} />}
			</MobileMenu>
		</>;
}